/**
 * 超融合应用页面
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import Layout from 'components/layout';
import TDK from 'components/tdk';
import MultipleBanner from 'components/product/banner/multiple';
import cn from 'classnames';
import Transition from 'components/transition';
import { Tabs } from 'antd';
import Contact from 'components/contact';
import UseResize from 'utils/hooks/useResize';
import { fusions, advantages, clients, urlPre, arrowIcon } from 'constants/platform';
import type { TimerRef } from 'utils/hooks/useScrollPlay';
import { clearTimer, handlePlay } from 'utils/hooks/useScrollPlay';
import CustomLink from 'components/link';
import style from './index.module.scss';
import TabPane from 'components/tabPane';

const SuperMerge = () => {
  const [logoActiveKey, setLogoActiveKey] = useState(0);
  const [cloudActiveKey, setCloudActiveKey] = useState(0);
  const timerRef1 = useRef<TimerRef>(null);
  const timerRef2 = useRef<TimerRef>(null);
  const logoContainer = useRef<HTMLDivElement>(null);
  const cloudContainer = useRef<HTMLDivElement>(null);
  const stopLogoAct = useRef(false);
  const stopCloudAct = useRef(false);
  const size = UseResize();
  //易部署部分
  const cloudsTotal = useRef(advantages[advantages.length - 1].icon.length);
  //底部logo
  const logoTotal = useRef(clients.length);

  const handleLogoPlay = useCallback(() => {
    handlePlay({
      timerRef: timerRef1,
      setActiveKey: setLogoActiveKey,
      stopAct: stopLogoAct,
      tabsTotal: logoTotal.current,
    });
  }, [logoTotal, setLogoActiveKey, stopLogoAct]);

  const handleCloudPlay = useCallback(() => {
    handlePlay({
      timerRef: timerRef2,
      setActiveKey: setCloudActiveKey,
      stopAct: stopCloudAct,
      tabsTotal: cloudsTotal.current,
    });
  }, [cloudsTotal, setCloudActiveKey, stopCloudAct]);

  const onScroll = useCallback(() => {
    const clientScreen = document.documentElement.clientHeight;

    const handleTabPlay = (containerRef: React.RefObject<HTMLDivElement>, playHandler: () => void) => {
      //需要轮播的区域出现的当前视口
      const { top } = containerRef?.current?.getBoundingClientRect() ?? {};
      if (top && top < clientScreen) {
        playHandler();
        if (logoContainer) {
          window.removeEventListener('scroll', onScroll);
        }
      }
    };

    handleTabPlay(logoContainer, handleLogoPlay);
    handleTabPlay(cloudContainer, handleCloudPlay);
  }, [handleLogoPlay, handleCloudPlay]);

  useEffect(() => {
    window.addEventListener('scroll', onScroll, true);
    return () => {
      // 注销
      window.removeEventListener('scroll', onScroll);
      clearTimer(timerRef1);
      clearTimer(timerRef2);
    };
  }, [onScroll]);

  /* 无滚动时 触发动画*/
  useEffect(() => {
    if (!logoContainer.current?.scrollTop || !cloudContainer.current?.scrollTop) {
      onScroll();
    }
  }, [onScroll]);

  return (
    <Layout>
      <TDK pageKey="platform" />
      <div className={style.container}>
        {/* 头部 */}
        <div className={style.header}>
          <MultipleBanner
            type={0}
            mUrl={urlPre + 'mobile/%E5%A4%B4%E5%9B%BE-M.jpg'}
            pcUrl={urlPre + '%E5%A4%B4%E5%9B%BE.jpg'}
            title={'超融合视频平台'}
            desc={`采用新一代智能云化架构和先进的音视频算法，功能丰富，性能强大，部署灵活，易管易用，\n为用户提供安全、稳定、高品质的音视频体验。`}
            mDesc={`采用新一代智能云化架构和先进的音视频算法，\n功能丰富，性能强大，部署灵活，易管易用，\n为用户提供安全、稳定、高品质的音视频体验。`}
            className={cn(style.banner, style.banner2, style.banner4)}
            delay={0.5}
            animate={'fadeIn'}
          ></MultipleBanner>
        </div>
        {/* 无所不融 */}
        <div className={style.fusions}>
          <img className={style.arrow} src={arrowIcon[0]} alt="" />
          <Transition delay={0.5}>
            <p className={style.title}>{`无所不“融”\n集大成，视频应用，不再设限！`}</p>
          </Transition>
          <div className={style.content}>
            {fusions.map((item: any, index: number) => {
              return (
                <div className={cn(style.item)} key={item.title}>
                  <div className={style.itemTitle}>{item.title}</div>
                  <img className={style.itemArrow} src={arrowIcon[3]} alt="" />
                  <div className={cn(style.itemContent, index === 0 && style.flatItem)}>
                    <div className={cn(style['for-pc'], style.desc)} dangerouslySetInnerHTML={{ __html: `${item.desc}`.replace(/\n/g, '<br/>') }}></div>
                    <div className={cn(style['for-mobile'], style.desc)} dangerouslySetInnerHTML={{ __html: `${item.mDesc}`.replace(/\n/g, '') }}></div>
                    <img className={cn(style['for-pc'], style.icon)} src={item.icon} alt="" />
                    <img className={cn(style['for-mobile'], style.icon)} src={item.mIcon} alt="" />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        {/* 一应俱全 */}
        <div className={style.advantages}>
          <img className={style.topArrow} src={arrowIcon[1]} alt="" />
          <img className={style.bottomArrow} src={arrowIcon[2]} alt="" />
          <Transition delay={0.5}>
            <p className={style.title}>{`一应俱全\n给你酣畅淋漓的沉浸体验！`}</p>
          </Transition>
          <div className={style.content}>
            {advantages.map((item: any, index: number) => {
              const desc = size.width < 768 ? item.mDesc : item.desc;
              const lastItem = advantages.length - 1;
              return (
                <div className={style.item} key={item.title}>
                  <div className={style.itemContent}>
                    <Transition delay={0.8}>
                      <div className={style.left}>
                        <div className={style.itemTitle}>{item.title}</div>
                        <div className={cn(style.desc)}>
                          {desc.map((item: any, index: number) => {
                            return (
                              <p className={style.dot} key={index}>
                                {item}
                              </p>
                            );
                          })}
                        </div>
                      </div>
                    </Transition>
                    {/* 最后一个需要tab轮播 */}
                    {index === lastItem ? (
                      <div className={style.clouds} ref={cloudContainer}>
                        <Tabs
                          tabPosition={'bottom'}
                          type={'card'}
                          activeKey={cloudActiveKey.toString()}
                          onTabClick={(activeKey: string) => {
                            stopCloudAct.current = true;
                            setCloudActiveKey(Number(activeKey));
                            clearTimer(timerRef2);
                          }}
                        >
                          {item.icon.map((item: any, index: number) => (
                            <Tabs.TabPane tab={item.topic} key={index}>
                              <div className={style.cloudItem}>
                                <img className={style.icon} src={item.icon} alt="" />
                                <div className={style.simpleDesc}>{item.desc}</div>
                              </div>
                            </Tabs.TabPane>
                          ))}
                        </Tabs>
                      </div>
                    ) : (
                      <img className={style.icon} src={item.icon} alt="" />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        {/* 一路相伴 */}
        <div className={cn(style.clients, logoActiveKey > 2 && style.changeBar)}>
          <img className={style.arrow} src={arrowIcon[4]} alt="" />
          <Transition delay={0.5}>
            <p className={style.mainTitle}>{`一路相伴\n16W+客户的信赖选择！`}</p>
            <p
              className={cn(style['for-pc'], style.explain)}
            >{`超融合视频平台通过构建“纵向到底、横向到边”的云视频应用，全面打破信息孤岛，实现大容量、高频次、高品质、\n跨单位、跨部门、便捷快速的高效协作，助力各行各业数字化转型升级。`}</p>
            <p
              className={cn(style['for-mobile'], style.explain)}
            >{`超融合视频平台通过构建“纵向到底、横向到边”的\n云视频应用，全面打破信息孤岛，实现大容量、高\n频次、高品质、跨单位、跨部门、便捷快速的高效\n协作，助力各行各业数字化转型升级。`}</p>
          </Transition>

          <div ref={logoContainer}>
            <Tabs
              className={style.client}
              activeKey={logoActiveKey.toString()}
              onTabClick={(activeKey: string) => {
                stopLogoAct.current = true;
                setLogoActiveKey(Number(activeKey));
                clearTimer(timerRef1);
              }}
            >
              {TabPane({ list: clients, activeKey: logoActiveKey, logoClass: style.logo })}
            </Tabs>
          </div>
          <Contact className={cn(style['contact-footer'])} btnType={'newBtn'} />
        </div>
        <CustomLink isNextLink={false} href="/statement2" className={style.statement}>
          <img src="https://devcdn.xylink.com/xylink_website_doc/images/product/cloud/pc/warn-icon.png" alt="" />
          <span>关于加强安全专项治理的说明</span>
        </CustomLink>
      </div>
    </Layout>
  );
};
export default SuperMerge;
